<template>
  <Breadcrumbs :breadcrumbs="breadcrumbs" />
  <BaseCustomHTMLContent
    class="container"
    :custom-html-content="data?.websitePage?.content"
  />
</template>

<script setup lang="ts">
import { useRouteParams } from "@vueuse/router";
import { useWebsitePageQuery } from "~/graphql/generated";

definePageMeta({
  disableHrefLangs: true,
});

const path = useRouteParams<string>("slug");

const { locale } = useI18n();
const { data, error, executeQuery } = await useWebsitePageQuery({
  variables: {
    websitePage: {
      path: `/${path.value}`,
    },
  },
  requestPolicy: "cache-and-network",
});

if (error?.value || !data.value?.websitePage) {
  throw createError({ statusCode: 404, fatal: true });
}

// Set the breadcrumbs
const breadcrumbs = computed(() => [
  {
    to: data.value?.websitePage?.path ?? "/",
    text: data.value?.websitePage?.title ?? "",
  },
]);

watch(
  locale,
  () => {
    // Refetch page content when the language changes to avoid stale localized content.
    executeQuery({ requestPolicy: "network-only" });
  },
  { flush: "post" },
);

const { currentHrefLangs } = useContentHrefLangs(
  computed(() => data.value?.websitePage),
);

const pageTitle = computed(() => data.value?.websitePage?.metaTitle || data.value?.websitePage?.title || undefined);
const pageDescription = computed(() => data.value?.websitePage?.metaDescription || undefined);

useHead(() => ({
  title: pageTitle.value,
  meta: [
    {
      name: "description",
      content: pageDescription.value,
    },
  ],
  link: currentHrefLangs.value.map((link) => ({
    id: link.hid,
    rel: link.rel,
    href: link.href,
    hreflang: link.hreflang,
  })),
}));

useSeoMeta({
  ogTitle: pageTitle,
  ogDescription: pageDescription,
});
</script>

<style lang="scss" scoped>
.container:deep(img) {
  max-width: 100%;
}
</style>
